// One component per exercise mode.
//
// Each takes an exercise and reports what the learner did through `onAnswer`,
// which hands back either typed text or an explicit grade. Nothing here talks
// to the API or knows about scheduling -- LessonRunner owns all of that, so the
// same components are reusable by the games.

import { useEffect, useRef, useState } from 'react'

import type { Exercise } from '../../api/types'
import { speak } from '../../lib/speech'
import { sfx } from '../../lib/sound'
import { Button, Input } from '../ui'

export interface AnswerPayload {
  given?: string
  grade?: number
}

export interface ExerciseProps {
  exercise: Exercise
  locale: string | null
  disabled: boolean
  onAnswer: (payload: AnswerPayload) => void
}

/** Digits 1-4 select an option, so a whole lesson can be done from the keyboard. */
function useNumberKeys(count: number, disabled: boolean, pick: (i: number) => void) {
  useEffect(() => {
    if (disabled) return
    function onKey(event: KeyboardEvent) {
      const index = Number(event.key) - 1
      if (Number.isInteger(index) && index >= 0 && index < count) {
        event.preventDefault()
        pick(index)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [count, disabled, pick])
}

// --- multiple choice ------------------------------------------------------

export function MultipleChoice({ exercise, disabled, onAnswer }: ExerciseProps) {
  const choose = (index: number) => {
    if (disabled) return
    onAnswer({ given: exercise.choices[index] })
  }
  useNumberKeys(exercise.choices.length, disabled, choose)

  return (
    <div className="space-y-3">
      <p className="text-center text-3xl font-semibold tracking-tight">
        {exercise.prompt}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {exercise.choices.map((choice, index) => (
          <button
            key={choice}
            disabled={disabled}
            onClick={() => choose(index)}
            className="flex items-center gap-3 rounded-xl border border-border-subtle bg-surface
              px-4 py-3 text-left transition hover:border-brand disabled:opacity-60"
          >
            <span className="grid size-6 shrink-0 place-items-center rounded-md border
              border-border-subtle text-xs text-muted">
              {index + 1}
            </span>
            <span className="font-medium">{choice}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// --- typing ---------------------------------------------------------------

export function Typing({ exercise, disabled, onAnswer }: ExerciseProps) {
  const [value, setValue] = useState('')
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setValue('')
    ref.current?.focus()
  }, [exercise])

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!disabled) onAnswer({ given: value })
      }}
      className="space-y-3"
    >
      <p className="text-center text-3xl font-semibold tracking-tight">
        {exercise.prompt}
      </p>
      <Input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        placeholder="Type your answer"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="text-center text-lg"
      />
      <Button type="submit" className="w-full" disabled={disabled}>
        Check
      </Button>
    </form>
  )
}

// --- scramble -------------------------------------------------------------

export function Scramble({ exercise, disabled, onAnswer }: ExerciseProps) {
  // Indices into exercise.tiles, so repeated letters stay distinguishable --
  // tracking by character would make the two "s"s in "gladius" interchangeable
  // and let one disappear.
  const [used, setUsed] = useState<number[]>([])

  useEffect(() => setUsed([]), [exercise])

  const built = used.map((i) => exercise.tiles[i]).join('')
  const remaining = exercise.tiles
    .map((tile, index) => ({ tile, index }))
    .filter(({ index }) => !used.includes(index))

  return (
    <div className="space-y-4">
      <p className="text-center text-sm text-muted">{exercise.prompt}</p>

      {/* What has been built so far. */}
      <div
        className="flex min-h-14 flex-wrap items-center justify-center gap-1 rounded-xl
          border-2 border-dashed border-border-subtle px-3 py-2"
      >
        {used.length === 0 ? (
          <span className="text-sm text-muted">Tap the letters in order</span>
        ) : (
          used.map((tileIndex, position) => (
            <button
              key={`${tileIndex}-${position}`}
              disabled={disabled}
              onClick={() => setUsed(used.slice(0, position))}
              className="rounded-lg bg-brand px-3 py-2 font-mono text-lg font-semibold text-white"
            >
              {exercise.tiles[tileIndex]}
            </button>
          ))
        )}
      </div>

      {/* The pool still to be used. */}
      <div className="flex flex-wrap justify-center gap-1">
        {remaining.map(({ tile, index }) => (
          <button
            key={index}
            disabled={disabled}
            onClick={() => {
              sfx.tick()
              setUsed([...used, index])
            }}
            className="rounded-lg border border-border-subtle bg-surface px-3 py-2
              font-mono text-lg transition hover:border-brand disabled:opacity-60"
          >
            {tile}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={() => setUsed([])}
          disabled={disabled || used.length === 0}
        >
          Clear
        </Button>
        <Button
          className="flex-1"
          disabled={disabled || used.length === 0}
          onClick={() => onAnswer({ given: built })}
        >
          Check
        </Button>
      </div>
    </div>
  )
}

// --- listen ---------------------------------------------------------------

export function Listen({ exercise, locale, disabled, onAnswer }: ExerciseProps) {
  const [value, setValue] = useState('')
  const ref = useRef<HTMLInputElement>(null)

  // Speak once on arrival: the whole exercise is "what did you just hear".
  useEffect(() => {
    setValue('')
    speak(exercise.speak ?? '', { locale })
    ref.current?.focus()
  }, [exercise, locale])

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!disabled) onAnswer({ given: value })
      }}
      className="space-y-4"
    >
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => speak(exercise.speak ?? '', { locale })}
          className="grid size-20 place-items-center rounded-full bg-brand text-white
            transition hover:opacity-90"
          aria-label="Play the word again"
        >
          <svg viewBox="0 0 24 24" className="size-9" fill="currentColor" aria-hidden>
            <path d="M4 9v6h4l5 4V5L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 2v2a8 8 0 0 1 0 16v2a10 10 0 0 0 0-20z" />
          </svg>
        </button>
      </div>
      <p className="text-center text-sm text-muted">What does it mean?</p>

      <Input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        placeholder="Type the meaning"
        autoComplete="off"
        spellCheck={false}
        className="text-center text-lg"
      />
      <Button type="submit" className="w-full" disabled={disabled}>
        Check
      </Button>
    </form>
  )
}

// --- true / false ---------------------------------------------------------

export function TrueFalse({ exercise, disabled, onAnswer }: ExerciseProps) {
  // Graded here rather than by text comparison: the question is whether the
  // pairing shown is right, so the answer is a judgement, not a word.
  const respond = (saidMatch: boolean) => {
    if (disabled) return
    const right = saidMatch === exercise.is_match
    onAnswer({ grade: right ? 5 : 1 })
  }

  useNumberKeys(2, disabled, (i) => respond(i === 0))

  return (
    <div className="space-y-5">
      <p className="text-center text-sm text-muted">Do these mean the same thing?</p>
      <div className="flex items-center justify-center gap-3 text-2xl font-semibold">
        <span>{exercise.prompt}</span>
        <span className="text-muted">=</span>
        <span className="text-brand">{exercise.shown}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" disabled={disabled} onClick={() => respond(true)}>
          Yes
        </Button>
        <Button variant="secondary" disabled={disabled} onClick={() => respond(false)}>
          No
        </Button>
      </div>
    </div>
  )
}

// --- flashcard (self-graded) ----------------------------------------------

const SELF_GRADES = [
  { label: 'Again', grade: 1 },
  { label: 'Hard', grade: 3 },
  { label: 'Good', grade: 4 },
  { label: 'Easy', grade: 5 },
]

export function Flashcard({ exercise, disabled, onAnswer }: ExerciseProps) {
  const [revealed, setRevealed] = useState(false)
  useEffect(() => setRevealed(false), [exercise])

  return (
    <div className="space-y-5">
      <p className="text-center text-3xl font-semibold tracking-tight">
        {exercise.prompt}
      </p>

      {revealed ? (
        <>
          <p className="text-center text-2xl text-brand">{exercise.answer}</p>
          <div className="grid grid-cols-4 gap-2">
            {SELF_GRADES.map(({ label, grade }) => (
              <Button
                key={label}
                variant="secondary"
                disabled={disabled}
                onClick={() => onAnswer({ grade })}
              >
                {label}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <Button className="w-full" onClick={() => setRevealed(true)}>
          Show answer
        </Button>
      )}
    </div>
  )
}

// --- dispatch -------------------------------------------------------------

export function ExerciseView(props: ExerciseProps) {
  switch (props.exercise.mode) {
    case 'multiple_choice':
      return <MultipleChoice {...props} />
    case 'scramble':
      return <Scramble {...props} />
    case 'listen':
      return <Listen {...props} />
    case 'true_false':
      return <TrueFalse {...props} />
    case 'typing':
    default:
      return <Typing {...props} />
  }
}
